import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import ListPageHeader from '../common/ListPageHeader.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import {
  Users,
  Pencil,
  ArrowLeft,
  IndianRupee,
  FileText,
  Send,
  MoreVertical,
} from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';

function InfoField({ label, value, className = '' }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className={className}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-words">
        {value}
      </p>
    </div>
  );
}

function SectionCard({ title, children, className = 'mb-4 sm:mb-6' }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 sm:p-5 ${className}`}>
      <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        {title}
      </h2>
      {children}
    </div>
  );
}

const ViewShipmentClient = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent';
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const moreActionsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  useEffect(() => {
    if (!showMoreActions) return undefined;

    const handleClickOutside = (event) => {
      if (moreActionsRef.current && !moreActionsRef.current.contains(event.target)) {
        setShowMoreActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreActions]);

  const closeMoreActions = () => setShowMoreActions(false);

  const fetchClient = async () => {
    try {
      const response = await shipmentClientService.getById(clientId);
      setClient(response.data);
    } catch (err) {
      Alert.error('Failed to load client details');
      console.error(err);
      navigate('/shipment-clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setDownloading(true);
      await shipmentClientService.downloadClientInvoice(clientId);
      Alert.success('Invoice downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download invoice');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendInvoice = async () => {
    const confirmed = await Alert.confirm(
      `Send invoice to ${client.email}?`,
      'Send Invoice'
    );

    if (confirmed) {
      try {
        setSending(true);
        await shipmentClientService.sendClientInvoice(clientId);
        Alert.success('Invoice sent successfully!');
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to send invoice');
      } finally {
        setSending(false);
      }
    }
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => `₹${Number(amount || 0).toFixed(2)}`;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading client details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-600 font-semibold text-base sm:text-lg">Client not found</p>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/shipment-clients')}
              className="mt-4"
            >
              Back to Clients
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <ListPageHeader
        title={t('pages.shipmentClient.view')}
        subtitle={client.full_name}
        icon={Users}
        backTo="/shipment-clients"
        titleClassName="text-secondary"
        iconClassName="w-5 h-5 text-secondary shrink-0"
        className="mb-4 sm:mb-6"
      >
        <span
          className={`hidden md:inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
            client.status === 'active'
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {capitalizeStatus(client.status)}
        </span>
        {!isAgent && (
        <Button
          variant="success"
          icon={IndianRupee}
          onClick={() => navigate(`/shipment-clients/${clientId}/batch-bill`)}
          className="hidden md:inline-flex"
        >
          Pay
        </Button>
        )}
        <Button
          variant="secondary"
          icon={Pencil}
          onClick={() => navigate(`/shipment-clients/edit/${clientId}`)}
          className="hidden md:inline-flex"
        >
          Edit Client
        </Button>
        <Button
          variant="secondary"
          icon={ArrowLeft}
          mobileIconOnly
          mobileLabel="Back"
          onClick={() => navigate('/shipment-clients')}
          className="hidden md:inline-flex"
        >
          Back
        </Button>

        <div ref={moreActionsRef} className="relative">
          <Button
            variant="primary"
            icon={MoreVertical}
            mobileIconOnly
            mobileLabel="More Actions"
            onClick={() => setShowMoreActions((open) => !open)}
            aria-expanded={showMoreActions}
          >
            More
          </Button>

          {showMoreActions && (
            <div className="absolute right-0 mt-1 w-52 bg-white shadow-lg rounded-xl border border-gray-200 z-50 overflow-hidden">
              {!isAgent && (
              <button
                type="button"
                onClick={() => {
                  closeMoreActions();
                  navigate(`/shipment-clients/${clientId}/batch-bill`);
                }}
                className="md:hidden w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition flex items-center gap-2"
              >
                <IndianRupee className="w-4 h-4 text-emerald-600" />
                Pay
              </button>
              )}
              <button
                type="button"
                onClick={() => {
                  closeMoreActions();
                  navigate(`/shipment-clients/edit/${clientId}`);
                }}
                className="md:hidden w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit Client
              </button>
              {!isAgent && (
              <button
                type="button"
                onClick={() => {
                  closeMoreActions();
                  handleDownloadInvoice();
                }}
                disabled={downloading}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition flex items-center gap-2 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                {downloading ? 'Downloading...' : 'Download Invoice'}
              </button>
              )}
              {!isAgent && (
              <button
                type="button"
                onClick={() => {
                  closeMoreActions();
                  handleSendInvoice();
                }}
                disabled={sending}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 transition flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Sending...' : 'Email Invoice'}
              </button>
              )}
            </div>
          )}
        </div>
      </ListPageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
          <div className="rounded-xl bg-blue-50 px-3 py-2.5 min-w-0">
            <p className="text-[11px] sm:text-xs font-medium text-blue-700/80">Total Amount</p>
            <p className="text-base sm:text-lg font-bold text-blue-700 tabular-nums truncate">
              {formatAmount(client.total_amount)}
            </p>
          </div>
          <div className="rounded-xl bg-green-50 px-3 py-2.5 min-w-0">
            <p className="text-[11px] sm:text-xs font-medium text-green-700/80">Paid Amount</p>
            <p className="text-base sm:text-lg font-bold text-green-700 tabular-nums truncate">
              {formatAmount(client.paid_amount)}
            </p>
          </div>
          <div className="rounded-xl bg-red-50 px-3 py-2.5 min-w-0">
            <p className="text-[11px] sm:text-xs font-medium text-red-700/80">Due Amount</p>
            <p className="text-base sm:text-lg font-bold text-red-600 tabular-nums truncate">
              {formatAmount(client.due_amount)}
            </p>
          </div>
          <div className="rounded-xl bg-gray-50 px-3 py-2.5 min-w-0">
            <p className="text-[11px] sm:text-xs font-medium text-gray-500">Status</p>
            <span
              className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                client.status === 'active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {capitalizeStatus(client.status)}
            </span>
          </div>
      </div>

      {client.due_amount > 0 && (
        <div className="mb-4 sm:mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-yellow-800">
              Outstanding Balance: {formatAmount(client.due_amount)}
            </p>
            <p className="text-xs text-yellow-600 mt-1">
              Process payments for unpaid shipments.
            </p>
          </div>
          <Button
            variant="success"
            icon={IndianRupee}
            onClick={() => navigate(`/shipment-clients/${clientId}/batch-bill`)}
            className="w-full sm:w-auto shrink-0"
          >
            Process Payment
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6 min-w-0">
        <SectionCard title="Basic Information" className="mb-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
            <InfoField label="Full Name" value={client.full_name} />
            <InfoField label="Company Name" value={client.company_name} />
            <InfoField label="GST Number" value={client.gst_number || 'N/A'} />
          </div>
        </SectionCard>

        <SectionCard title="Contact Information" className="mb-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
            <InfoField label="Email" value={client.email} />
            <InfoField label="Contact Number" value={client.contact} />
            <InfoField
              label={t('common.address')}
              value={client.address}
              className="sm:col-span-2"
            />
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Invoice & Statements">
        <p className="text-sm text-gray-600 mb-4">
          Download or email a complete statement of account for this client including all shipments and payment details.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="primary"
            icon={FileText}
            onClick={handleDownloadInvoice}
            disabled={downloading}
            loading={downloading}
            className="w-full sm:w-auto"
          >
            {downloading ? 'Downloading...' : 'Download Statement'}
          </Button>
          <Button
            variant="primary"
            icon={Send}
            onClick={handleSendInvoice}
            disabled={sending}
            loading={sending}
            className="w-full sm:w-auto"
          >
            {sending ? 'Sending...' : 'Email Statement'}
          </Button>
        </div>
        {client.email && (
          <p className="text-xs text-gray-500 mt-3 break-all">
            Statement will be sent to:{' '}
            <span className="font-medium text-gray-700">{client.email}</span>
          </p>
        )}
      </SectionCard>

      <SectionCard title="System Information" className="mb-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          <InfoField label="Client ID" value={client.id} />
          <InfoField label={t('common.createdBy')} value={client.created_by || 'N/A'} />
          <InfoField label="Created At" value={formatDate(client.created_at)} />
          <InfoField label="Last Updated" value={formatDate(client.updated_at)} />
        </div>
      </SectionCard>
    </DashboardLayout>
  );
};

export default ViewShipmentClient;
